/**
 * Floating toolbar for the crest-line "Draw centreline" sketch.
 *
 * Replaces the text-only hint bar the dialog used to collapse into
 * ("Enter to finish · Esc to cancel"), which was keyboard-only and looked
 * nothing like the rest of the viewer's picking tools. Structure, ordering
 * and intents follow `AddLineToolbar` / `AddPolygonToolbar`: a status pill
 * over an icon row, Undo first, then a separator, then the primary commit
 * and the danger cancel.
 *
 * It must be mounted as a SIBLING of the `<Canvas>` in `Viewer.tsx`, never
 * inside it — `FloatingToolbar` renders plain DOM, which R3F's reconciler
 * cannot host. `FloatingToolbarButton` already stops click and pointerdown
 * propagation, which is what keeps a button press from also landing as a
 * guide vertex on the canvas underneath.
 *
 * Every button goes through the shared action atoms in `crestLine.atom.ts`,
 * which are the exact same writes `CrestGuideTool`'s Backspace / Enter /
 * Escape handlers perform — Finish is Enter, Cancel is Escape, by
 * construction rather than by two implementations agreeing.
 */
import {
  CREST_GUIDE_MIN_VERTICES,
  crestGuideCancelAtom,
  crestGuideDraftAtom,
  crestGuideFinishAtom,
  crestGuideModeAtom,
  crestGuideUndoVertexAtom,
} from '@/atoms/crestLine.atom';
import { Check, Trash, Undo } from '@mynaui/icons-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { FloatingToolbar, FloatingToolbarButton, FloatingToolbarSeparator } from './FloatingToolbar';

export const CrestGuideToolbar = () => {
  const { t } = useTranslation('project');
  const mode = useAtomValue(crestGuideModeAtom);
  const draft = useAtomValue(crestGuideDraftAtom);
  const undoVertex = useSetAtom(crestGuideUndoVertexAtom);
  const finishSketch = useSetAtom(crestGuideFinishAtom);
  const cancelSketch = useSetAtom(crestGuideCancelAtom);

  if (!mode) return null;

  // `guideStations` bails below 2 vertices, so a shorter sketch cannot
  // produce a crest — Finish stays disabled rather than committing a guide
  // the dialog's own `draft.length < 2` guard would then silently drop.
  // This is the AFFORDANCE only; the rule itself lives in
  // `crestGuideFinishAtom`, which no-ops under the same constant, so the
  // keyboard path (Enter, in `CrestGuideTool`) cannot bypass it.
  const canFinish = draft.length >= CREST_GUIDE_MIN_VERTICES;

  return (
    <FloatingToolbar status={t('crestLine.guideDrawStatus', { count: draft.length })}>
      <FloatingToolbarButton
        icon={<Undo />}
        tooltip={t('crestLine.guideUndoPoint')}
        disabled={draft.length === 0}
        onClick={undoVertex}
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<Check />}
        tooltip={t('crestLine.guideFinish')}
        disabled={!canFinish}
        intent='primary'
        primaryClass='bg-emerald-600 hover:bg-emerald-700'
        onClick={finishSketch}
      />
      <FloatingToolbarButton
        icon={<Trash />}
        tooltip={t('crestLine.guideCancel')}
        intent='danger'
        onClick={cancelSketch}
      />
    </FloatingToolbar>
  );
};
